import { useCallback, useState } from 'react'
import {
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  rectIntersection,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { useSnackbar } from '../../../../../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../../../../../components/stories/molecules/snackbar/constants'
import { useMoveProjectsAndFoldersMutation } from '../../../../../../../react-query/project/mutation/use-move-projects-and-folders-mutation'
import { projectMenuApiErrorCodeOptions } from '../../../../../constants'
import {
  getActiveItem,
  getExpandedWithFolder,
  getItemsToMove,
  getMovableItemsToFolder,
  getMovableItemsToRoot,
  getMovePayload,
  projectMenuDropTargetTypeOptions
} from '../utils'

const getFolderCollisions = (collisions) =>
  collisions.filter(
    (collision) =>
      collision.data?.droppableContainer?.data.current?.type ===
      projectMenuDropTargetTypeOptions.FOLDER
  )

const getRootRowCollisions = (collisions) =>
  collisions.filter(
    (collision) =>
      collision.data?.droppableContainer?.data.current?.type ===
      projectMenuDropTargetTypeOptions.ROOT_ROW
  )

const getRowCollisions = (collisions) =>
  collisions.filter(
    (collision) =>
      collision.data?.droppableContainer?.data.current?.type ===
      projectMenuDropTargetTypeOptions.ROW
  )

const isConflictError = (error) =>
  error?.response?.data?.status ===
    projectMenuApiErrorCodeOptions.PROJECT_NAME_IS_NOT_AVAILABLE ||
  error?.response?.status ===
    projectMenuApiErrorCodeOptions.PROJECT_NAME_IS_NOT_AVAILABLE

export const useCompanyProjectsDnd = ({
  formatMessage,
  rowSelection,
  rowsById,
  setExpanded
}) => {
  const { addSnackbar } = useSnackbar()
  const [movingItemIds, setMovingItemIds] = useState([])

  const { mutate: moveItems, isPending: isMovingItems } =
    useMoveProjectsAndFoldersMutation({
      onSuccess: () => {
        addSnackbar({
          message: formatMessage({
            id: 'moveItems_items_moved',
            defaultMessage: 'Items moved'
          }),
          snackbarType: snackbarVariantOptions.SUCCESS,
          hideCloseButton: true
        })
      },
      onError: (error, variables) => {
        setMovingItemIds([])

        const messageId = isConflictError(error)
          ? variables?.projectIds?.length
            ? 'projectMenu_project_with_this_name_aready_exists'
            : 'projectMenu_folder_with_this_name_aready_exists'
          : 'moveItems_move_error'

        addSnackbar({
          message: formatMessage({ id: messageId }),
          snackbarType: snackbarVariantOptions.ERROR,
          hideCloseButton: true,
          timeToHide: 5000
        })
      },
      onSettled: () => {
        setMovingItemIds([])
      }
    })

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  )

  const collisionDetection = useCallback((args) => {
    const collisions = rectIntersection(args)
    const folderCollisions = getFolderCollisions(collisions)
    const rootRowCollisions = getRootRowCollisions(collisions)
    const rowCollisions = getRowCollisions(collisions)

    if (folderCollisions.length) return folderCollisions
    if (rootRowCollisions.length) return rootRowCollisions
    if (rowCollisions.length) return rowCollisions

    return collisions
  }, [])

  const openFolder = useCallback(
    (folderId) => {
      setExpanded((currentExpanded) =>
        getExpandedWithFolder(currentExpanded, folderId)
      )
    },
    [setExpanded]
  )

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      const dropTargetType = over?.data.current?.type
      const destinationFolder =
        dropTargetType === projectMenuDropTargetTypeOptions.FOLDER
          ? over.data.current.folder
          : null
      const isRootDrop = [
        projectMenuDropTargetTypeOptions.ROOT,
        projectMenuDropTargetTypeOptions.ROOT_ROW
      ].includes(dropTargetType)
      const activeItem = getActiveItem(active, rowsById)

      if (!activeItem || (!destinationFolder && !isRootDrop)) return

      const itemsToMove = getItemsToMove({ activeItem, rowSelection, rowsById })
      const movableItems = destinationFolder
        ? getMovableItemsToFolder(itemsToMove, destinationFolder)
        : getMovableItemsToRoot(itemsToMove)

      if (!movableItems.length) return

      if (destinationFolder) {
        openFolder(destinationFolder.id)
      }

      setMovingItemIds(movableItems.map(({ id }) => String(id)))

      moveItems({
        ...getMovePayload(movableItems),
        destinationFolderId: destinationFolder?.id ?? null
      })
    },
    [moveItems, openFolder, rowSelection, rowsById]
  )

  return {
    collisionDetection,
    handleDragEnd,
    isMovingItems,
    movingItemIds,
    sensors
  }
}
